import { CheckIcon, ChevronDownIcon } from 'lucide-react';
import React, { useMemo, useState } from 'react';

import { cn } from '@/lib/utils';
import {
  FirmTaskTemplateResponse,
  InternalTaskFilter,
  useFirmTaskTemplatesListQuery,
} from '@/services/api/firmsService';
import {
  TaskType,
  useGetTaskGroupsQuery,
  useGetTaskTypesQuery,
} from '@/services/api/tasksService';

interface InternalTaskFiltersProps {
  value: InternalTaskFilter;
  onChange: (value: InternalTaskFilter) => void;
  disabled?: boolean;
  firmId: string;
}

interface FilterOptionProps {
  label: string;
  isSelected: boolean;
  onClick: () => void;
  disabled?: boolean;
}

const FilterOption: React.FC<FilterOptionProps> = ({
  label,
  isSelected,
  onClick,
  disabled,
}) => (
  <button
    type="button"
    onClick={onClick}
    disabled={disabled}
    className={cn(
      'group flex w-full items-center gap-3 rounded-lg px-3 py-2.5 text-left transition-all duration-150',
      disabled
        ? 'cursor-not-allowed opacity-50'
        : 'hover:bg-slate-50 active:bg-slate-100',
    )}
  >
    <div
      className={cn(
        'flex h-5 w-5 shrink-0 items-center justify-center rounded-md border-2 transition-all duration-150',
        isSelected
          ? 'border-emerald-500 bg-emerald-500'
          : 'border-slate-300 bg-white group-hover:border-slate-400',
        disabled && 'border-slate-200 bg-slate-50',
      )}
    >
      {isSelected && (
        <CheckIcon className="h-3.5 w-3.5 text-white" strokeWidth={3} />
      )}
    </div>
    <span
      className={cn(
        'text-sm font-medium transition-colors',
        isSelected ? 'text-slate-900' : 'text-slate-600',
        disabled && 'text-slate-400',
      )}
    >
      {label}
    </span>
  </button>
);

interface CollapsibleSectionProps {
  title: string;
  count: number;
  selectedCount: number;
  children: React.ReactNode;
  defaultOpen?: boolean;
}

const CollapsibleSection: React.FC<CollapsibleSectionProps> = ({
  title,
  count,
  selectedCount,
  children,
  defaultOpen = false,
}) => {
  const [isOpen, setIsOpen] = useState(defaultOpen);

  return (
    <div className="overflow-hidden rounded-xl border border-slate-200 bg-white">
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className="flex w-full items-center justify-between px-4 py-3 transition-colors hover:bg-slate-50"
      >
        <div className="flex items-center gap-3">
          <span className="text-sm font-semibold text-slate-900">{title}</span>
          <span className="rounded-full bg-slate-100 px-2 py-0.5 text-xs font-medium text-slate-500">
            {count}
          </span>
        </div>
        <div className="flex items-center gap-2">
          {selectedCount > 0 && (
            <span className="rounded-full bg-emerald-100 px-2 py-0.5 text-xs font-semibold text-emerald-700">
              {selectedCount} selected
            </span>
          )}
          <ChevronDownIcon
            className={cn(
              'h-5 w-5 text-slate-400 transition-transform duration-200',
              isOpen && 'rotate-180',
            )}
          />
        </div>
      </button>
      <div
        className={cn(
          'grid transition-all duration-200 ease-out',
          isOpen ? 'grid-rows-[1fr]' : 'grid-rows-[0fr]',
        )}
      >
        <div className="overflow-hidden">
          <div className="border-t border-slate-100 px-1 py-1">{children}</div>
        </div>
      </div>
    </div>
  );
};

export const InternalTaskFilters: React.FC<InternalTaskFiltersProps> = ({
  value,
  onChange,
  disabled = false,
  firmId,
}) => {
  const { data: firmTaskTemplates, isLoading: isLoadingTemplates } =
    useFirmTaskTemplatesListQuery({ firmId });
  const { data: taskGroups, isLoading: isLoadingGroups } =
    useGetTaskGroupsQuery();
  const { data: taskTypes, isLoading: isLoadingTypes } = useGetTaskTypesQuery();

  const isLoading = isLoadingTemplates || isLoadingGroups || isLoadingTypes;

  // Filter to only show active templates (not archived)
  const activeTemplates = useMemo(
    () => (firmTaskTemplates || []).filter((t) => t.status !== 'archived'),
    [firmTaskTemplates],
  );

  // Group templates by task_group
  const templatesByGroup = useMemo(() => {
    const grouped = new Map<string, FirmTaskTemplateResponse[]>();
    activeTemplates.forEach((template) => {
      const groupKey = template.task_group || 'Other';
      const existing = grouped.get(groupKey) || [];
      grouped.set(groupKey, [...existing, template]);
    });
    return grouped;
  }, [activeTemplates]);

  // Create a map from task group ID to name for lookup
  const taskGroupNameMap = useMemo(() => {
    const map = new Map<string, string>();
    (taskGroups || []).forEach((group) => {
      map.set(group.id, group.name);
    });
    return map;
  }, [taskGroups]);

  // Group task types by their group field (which is an ID), but use the name for display
  const typesByGroup = useMemo(() => {
    const grouped = new Map<string, { name: string; types: TaskType[] }>();
    (taskTypes || []).forEach((type) => {
      const groupId = type.group || 'other';
      const groupName = taskGroupNameMap.get(groupId) || 'Other';
      const existing = grouped.get(groupId) || { name: groupName, types: [] };
      grouped.set(groupId, {
        name: groupName,
        types: [...existing.types, type],
      });
    });
    return grouped;
  }, [taskTypes, taskGroupNameMap]);

  const updateField = <K extends keyof InternalTaskFilter>(
    field: K,
    fieldValue: InternalTaskFilter[K],
  ) => {
    if (disabled) return;
    onChange({ ...value, [field]: fieldValue });
  };

  const toggleTaskGroup = (groupId: string) => {
    if (disabled) return;
    const current = value.task_group_ids || [];
    if (current.includes(groupId)) {
      updateField(
        'task_group_ids',
        current.filter((id: string) => id !== groupId),
      );
    } else {
      updateField('task_group_ids', [...current, groupId]);
    }
  };

  const toggleTaskType = (typeId: string) => {
    if (disabled) return;
    const current = value.task_type_ids || [];
    if (current.includes(typeId)) {
      updateField(
        'task_type_ids',
        current.filter((id: string) => id !== typeId),
      );
    } else {
      updateField('task_type_ids', [...current, typeId]);
    }
  };

  const toggleTemplate = (templateId: string) => {
    if (disabled) return;
    const current = value.firm_task_template_ids || [];
    if (current.includes(templateId)) {
      updateField(
        'firm_task_template_ids',
        current.filter((id: string) => id !== templateId),
      );
    } else {
      updateField('firm_task_template_ids', [...current, templateId]);
    }
  };

  const selectedGroupsCount = value.task_group_ids?.length || 0;
  const selectedTypesCount = value.task_type_ids?.length || 0;
  const selectedTemplatesCount = value.firm_task_template_ids?.length || 0;
  const totalSelected =
    selectedGroupsCount + selectedTypesCount + selectedTemplatesCount;

  if (isLoading) {
    return (
      <div className="space-y-4">
        <div className="h-12 animate-pulse rounded-xl bg-slate-100" />
        <div className="h-12 animate-pulse rounded-xl bg-slate-100" />
      </div>
    );
  }

  return (
    <div
      className={cn('space-y-4', disabled && 'pointer-events-none opacity-60')}
    >
      {/* Task Groups */}
      {taskGroups && taskGroups.length > 0 && (
        <CollapsibleSection
          title="Task Groups"
          count={taskGroups.length}
          selectedCount={selectedGroupsCount}
          defaultOpen
        >
          <div className="grid grid-cols-2 gap-x-1">
            {taskGroups.map((group) => (
              <FilterOption
                key={group.id}
                label={group.name}
                isSelected={value.task_group_ids?.includes(group.id) || false}
                onClick={() => toggleTaskGroup(group.id)}
                disabled={disabled}
              />
            ))}
          </div>
        </CollapsibleSection>
      )}

      {/* Task Types */}
      {taskTypes && taskTypes.length > 0 && (
        <CollapsibleSection
          title="Task Types"
          count={taskTypes.length}
          selectedCount={selectedTypesCount}
          defaultOpen={false}
        >
          <div className="max-h-80 space-y-3 overflow-y-auto py-2">
            {Array.from(typesByGroup.entries()).map(
              ([groupId, { name, types }]) => (
                <div key={groupId}>
                  <div className="px-3 py-1.5">
                    <span className="text-xs font-semibold uppercase tracking-wider text-slate-400">
                      {name}
                    </span>
                  </div>
                  <div className="grid grid-cols-2 gap-x-1">
                    {types.map((type) => (
                      <FilterOption
                        key={type.id}
                        label={type.name}
                        isSelected={
                          value.task_type_ids?.includes(type.id) || false
                        }
                        onClick={() => toggleTaskType(type.id)}
                        disabled={disabled}
                      />
                    ))}
                  </div>
                </div>
              ),
            )}
          </div>
        </CollapsibleSection>
      )}

      {/* Firm Task Templates */}
      {activeTemplates.length > 0 && (
        <CollapsibleSection
          title="Firm Task Templates"
          count={activeTemplates.length}
          selectedCount={selectedTemplatesCount}
          defaultOpen={false}
        >
          <div className="max-h-80 space-y-3 overflow-y-auto py-2">
            {Array.from(templatesByGroup.entries()).map(
              ([groupName, templates]) => (
                <div key={groupName}>
                  <div className="px-3 py-1.5">
                    <span className="text-xs font-semibold uppercase tracking-wider text-slate-400">
                      {groupName}
                    </span>
                  </div>
                  <div className="grid grid-cols-2 gap-x-1">
                    {templates.map((template) => (
                      <FilterOption
                        key={template.id}
                        label={template.name}
                        isSelected={
                          value.firm_task_template_ids?.includes(template.id) ||
                          false
                        }
                        onClick={() => toggleTemplate(template.id)}
                        disabled={disabled}
                      />
                    ))}
                  </div>
                </div>
              ),
            )}
          </div>
        </CollapsibleSection>
      )}

      {/* Selection Summary */}
      {totalSelected > 0 && (
        <div className="flex items-center justify-between rounded-lg bg-slate-50 px-4 py-3">
          <span className="text-sm text-slate-600">
            <span className="font-semibold text-slate-900">
              {totalSelected}
            </span>{' '}
            filter{totalSelected !== 1 && 's'} active
          </span>
          <button
            type="button"
            onClick={() => {
              if (disabled) return;
              onChange({
                ...value,
                task_group_ids: [],
                task_type_ids: [],
                firm_task_template_ids: [],
              });
            }}
            disabled={disabled}
            className="text-sm font-medium text-slate-500 transition-colors hover:text-slate-700"
          >
            Clear all
          </button>
        </div>
      )}
    </div>
  );
};
